import { useMsal } from '@azure/msal-react';
import { isAuthBypassEnabled, isUserBypassEnabled } from './authSettings';
import { useCurrentAccess } from '@/hooks/useAccess';
import { useAdminPreview } from './adminPreview';

/** Application role names as issued via Azure AD app roles / ID token claims. */
export const AppRole = {
  Administrator: 'Administrator',
  FinanceAdmin: 'FinanceAdmin',
  IT: 'IT',
  ReadOnly: 'ReadOnly',
} as const;

export type AppRoleName = (typeof AppRole)[keyof typeof AppRole];

// Roles that get write access purely from their AAD app role, regardless of
// the per-user access assignment stored in the backend.
const WRITER_ROLES: AppRoleName[] = [AppRole.Administrator, AppRole.FinanceAdmin, AppRole.IT];
const adminGroupObjectId = import.meta.env.VITE_AAD_ADMIN_GROUP_ID?.trim().toLowerCase();

type AccountClaims = {
  roles?: string[];
  groups?: string[];
};

/**
 * Reads the application roles from the signed-in account's ID token claims and
 * combines them with the backend-managed per-user access assignment to derive
 * feature-level permission flags used throughout the SPA.
 *
 * Note: this is a client-side convenience for UI gating only; the server
 * enforces authorization independently.
 * @returns Role/permission flags for the current user, plus an access-loading flag.
 */
export function useRoles(): {
  roles: string[];
  canWrite: boolean;
  isAdmin: boolean;
  assetsAccess: boolean;
  contractsAccess: boolean;
  notificationsAccess: boolean;
  mobilityAccess: boolean;
  mobilityReviewer: boolean;
  mobilityPayroll: boolean;
  mobilityAdmin: boolean;
  mobilityAuditor: boolean;
  accessLoading: boolean;
} {
  const { accounts, instance } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0];
  const { data: access, isLoading: accessLoading } = useCurrentAccess();
  const adminPreview = useAdminPreview();

  // Bypass accounts get full administrator access, except that admin preview
  // mode can still be used to see the app as a non-admin would.
  if (isAuthBypassEnabled || isUserBypassEnabled(account)) {
    return {
      roles: [AppRole.Administrator],
      canWrite: true,
      isAdmin: !adminPreview,
      assetsAccess: true,
      contractsAccess: true,
      notificationsAccess: true,
      mobilityAccess: true,
      mobilityReviewer: true,
      mobilityPayroll: true,
      mobilityAdmin: true,
      mobilityAuditor: true,
      accessLoading: false,
    };
  }

  const claims = account?.idTokenClaims as AccountClaims | undefined;
  const roles = claims?.roles ?? [];
  const groups = claims?.groups?.map((groupId) => groupId.toLowerCase()) ?? [];
  const isInAdminGroup = Boolean(adminGroupObjectId) && groups.includes(adminGroupObjectId);
  // Server-reported isAdmin can be temporarily suppressed by admin preview mode.
  const isAdmin = (access?.isAdmin ?? false) && !adminPreview;

  // Each mobility sub-permission cascades: broader mobility roles (access/admin)
  // also grant the narrower ones (reviewer/payroll/auditor), matching the
  // backend's hierarchical mobility authorization policies.
  return {
    roles,
    canWrite: isAdmin || isInAdminGroup || roles.some((r) => WRITER_ROLES.includes(r as AppRoleName)),
    isAdmin,
    assetsAccess: isAdmin || (access?.assetsAccess ?? true),
    contractsAccess: isAdmin || (access?.contractsAccess ?? true),
    notificationsAccess: isAdmin || (access?.assetsAccess ?? true) || (access?.contractsAccess ?? true)
      || (access?.mobilityAccess ?? false) || (access?.mobilityReviewer ?? false)
      || (access?.mobilityPayroll ?? false) || (access?.mobilityAdmin ?? false) || (access?.mobilityAuditor ?? false),
    mobilityAccess: isAdmin || (access?.mobilityAdmin ?? false) || (access?.mobilityAccess ?? false)
      || (access?.mobilityReviewer ?? false) || (access?.mobilityPayroll ?? false) || (access?.mobilityAuditor ?? false),
    mobilityReviewer: isAdmin || (access?.mobilityAccess ?? false) || (access?.mobilityAdmin ?? false) || (access?.mobilityReviewer ?? false),
    mobilityPayroll: isAdmin || (access?.mobilityAccess ?? false) || (access?.mobilityAdmin ?? false) || (access?.mobilityPayroll ?? false),
    mobilityAdmin: isAdmin || (access?.mobilityAccess ?? false) || (access?.mobilityAdmin ?? false),
    mobilityAuditor: isAdmin || (access?.mobilityAccess ?? false) || (access?.mobilityAdmin ?? false) || (access?.mobilityAuditor ?? false),
    accessLoading,
  };
}
